import { useEffect } from 'react'
import { useMutation } from '@apollo/client'

import { useStore } from 'Stores/index'

import type {
  mutationUpdateUser,
  mutationUpdateUserVariables,
} from 'Constants/graphql/mutations/__generated__/mutationUpdateUser'
import { MUTATION_UPDATE_USER } from 'Constants/graphql/mutations/MutationUpdateUser'
import { AVAILABLE_LANGUAGES, SupportedLanguagesEnum } from 'Constants/locale'

import { Language } from 'Portal/__generated__/globalTypes'

import { pickFallbackLanguage } from './pickFallbackLanguage.util'

const SUPPORTED_LANGUAGE_TO_GQL: Record<SupportedLanguagesEnum, Language> = {
  [SupportedLanguagesEnum.DE]: Language.de,
  [SupportedLanguagesEnum.EN]: Language.en,
  [SupportedLanguagesEnum.FR]: Language.fr,
}

/**
 * Keeps the active portal language in sync with the organisation's
 * "enabled_languages" configuration. When the user's current language is
 * disabled for the organisation they are viewing, this hook switches the UI
 * to the highest-priority available language (DE \> EN \> FR) and persists the
 * new value via MUTATION_UPDATE_USER so the user doesn't land on a disabled
 * locale next time they sign in.
 *
 * Runs on app boot and whenever the active organisation changes (the
 * organisation store's enabledPortalLanguages computed will re-emit).
 * No-ops while the org context is still loading, since enabledPortalLanguages
 * falls back to "all three" until a real config arrives.
 */
export const usePortalLanguageFallback = () => {
  const { organisationStore, portalSettingsStore, userStore } = useStore()
  const { enabledPortalLanguages } = organisationStore
  const { i18n, setI18n } = portalSettingsStore
  const { user } = userStore

  const [updateUser] = useMutation<
    mutationUpdateUser,
    mutationUpdateUserVariables
  >(MUTATION_UPDATE_USER)

  useEffect(() => {
    if (enabledPortalLanguages.has(i18n.language as SupportedLanguagesEnum)) {
      return
    }

    const fallback = pickFallbackLanguage(enabledPortalLanguages)

    if (!fallback) {
      return
    }

    setI18n(AVAILABLE_LANGUAGES[fallback])

    if (user.userId) {
      updateUser({
        variables: {
          input: {
            id: user.userId.toString(),
            language: SUPPORTED_LANGUAGE_TO_GQL[fallback],
          },
        },
      })
    }

    if (typeof window.Intercom === 'function') {
      window.Intercom('update', {
        language_override: fallback === SupportedLanguagesEnum.DE ? 'de' : 'en',
      })
    }
  }, [
    enabledPortalLanguages,
    i18n.language,
    setI18n,
    updateUser,
    user.userId,
  ])
}
